import { useTranslation } from "react-i18next";
import { useGetTrainingUsageQuery } from "../services";

/**
 * The current organization's monthly training quota.
 *
 * The API is the real gate — this only exists so the UI can say how many runs are
 * left and stop a user filling in a whole model config they cannot submit. While
 * the usage is still loading `isExhausted` stays false, so the buttons are never
 * disabled on a guess.
 *
 * Two distinct blocked states: an org with a limit of 0 has no allowance at all
 * (nothing resets, an admin has to raise the flag), while an org that has spent
 * its allowance gets it back at `periodEnd`.
 */
export const useTrainingQuota = () => {
  const { t } = useTranslation(["model", "errors"]);
  const { data: usage } = useGetTrainingUsageQuery();

  const limit = usage?.limit ?? null;
  const used = usage?.used ?? 0;

  const hasNoAllowance = limit !== null && limit <= 0;
  const isExhausted = limit !== null && used >= limit;

  return {
    usage,
    /** True when the plan grants no trainings at all, so waiting will not help. */
    hasNoAllowance,
    /** True only when the org has a limit and has reached it. */
    isExhausted,
    /** Null for an unlimited org, which is the default — nothing to show. */
    label:
      limit === null
        ? null
        : hasNoAllowance
          ? t("quota.noAllowanceLabel")
          : t("quota.usage", { used, limit }),
    /** Why training is blocked, or null when it is not. */
    message:
      !isExhausted || !usage
        ? null
        : hasNoAllowance
          ? t("quota.noAllowanceMessage")
          : // The API's own catalog entry for refusing a dispatch, so a
            // client-side block and a server rejection read as the same rule.
            // `periodEnd` is the first instant of the next period, already UTC —
            // slicing keeps it that way, where formatting locally would show the
            // previous day for anyone west of UTC.
            t("errors:TRAINING_LIMIT_REACHED", {
              used,
              limit,
              resetsOn: usage.periodEnd.slice(0, 10),
            }),
    /** Short form for the disabled train button's tooltip. */
    blockedTitle: !isExhausted
      ? undefined
      : hasNoAllowance
        ? t("quota.noAllowanceTitle")
        : t("quota.limitReachedTitle"),
  };
};
